import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, Globe, ArrowRight, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { isStoresMobile } from '../lib/platform';
import { fmtCredits } from '../lib/currency';

/**
 * InsufficientBalanceDialog — shown when a buyer tries to spend wallet
 * credit they don't have (tips, paid posts, paid lives).
 *
 * UX policy (Phase 16):
 *   - On Web (desktop / non-store browser): show a clear "Go to Wallet"
 *     button that deep-links to /wallet for top-up.
 *   - On iOS / Android stores-mobile sessions: show NO clickable
 *     purchase link to comply with Apple/Google 30% royalty policy.
 *     Users are instructed to top up via the website only.
 *
 * Props:
 *   - open / onOpenChange
 *   - balance: current wallet balance in EUR (number)
 *   - required: amount the user tried to spend (number)
 *   - context: human-readable purchase context ('tip', 'post', 'ticket'…)
 */
const InsufficientBalanceDialog = ({ open, onOpenChange, balance = 0, required = 0, context = 'tip' }) => {
  const navigate = useNavigate();
  const mobile = isStoresMobile();
  const missing = Math.max(required - balance, 0);

  const ctxLabel = {
    tip: 'send this tip',
    post: 'unlock this post',
    ticket: 'buy this ticket',
  }[context] || 'complete this purchase';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="insufficient-balance-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <AlertTriangle className="h-5 w-5 gold-text" />
            Insufficient wallet balance
          </DialogTitle>
          <DialogDescription>
            Your Nymia wallet doesn&apos;t have enough credit to {ctxLabel}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 text-sm text-foreground">
          <div className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Your wallet</p>
              <p className="text-2xl font-bold gold-text" data-testid="ib-balance">{fmtCredits(balance)}</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Necessari</p>
              <p className="text-lg font-semibold">{fmtCredits(required)}</p>
              <p className="text-[11px] text-muted-foreground">mancano {fmtCredits(missing)}</p>
            </div>
          </div>

          {mobile ? (
            <div
              className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-foreground"
              data-testid="ib-mobile-message"
            >
              <Globe className="mt-0.5 h-4 w-4 shrink-0 gold-text" />
              <p className="text-[13px] leading-snug">
                Your balance is too low to {ctxLabel}.{' '}
                <span className="font-semibold">
                  To top up your wallet or add a credit card, please log in to your account from our web platform.
                </span>
              </p>
            </div>
          ) : (
            <div
              className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-foreground"
              data-testid="ib-web-message"
            >
              <Wallet className="mt-0.5 h-4 w-4 shrink-0 gold-text" />
              <p className="text-[13px] leading-snug">
                Top up your wallet to {ctxLabel}. Card payments and bank transfers
                are processed securely via Stripe on the Wallet page.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-row justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="ib-dismiss-btn"
          >
            Got it
          </Button>
          {!mobile && (
            <Button
              onClick={() => { onOpenChange(false); navigate('/wallet'); }}
              className="gold-gradient text-black"
              data-testid="ib-topup-btn"
            >
              Top up wallet <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InsufficientBalanceDialog;
